import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Button,
  Snackbar,
  IconButton,
} from "@mui/material";
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  CheckCircle as CheckCircleIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../store";
import { toggleFollowedPlayer } from "../../features/notifications/notificationSlice";
import { Players } from "../../types";

interface PlayerFollowListProps {
  players: Players[];
  userId: string;
}

const PlayerFollowList: React.FC<PlayerFollowListProps> = ({
  players,
  userId,
}) => {
  const dispatch = useAppDispatch();
  const { followedPlayers, loading, error } = useAppSelector(
    (state) => state.notifications
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<Players[]>([]);
  const [followedPlayersList, setFollowedPlayersList] = useState<Players[]>([]);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    action: "follow" | "unfollow";
  }>({ open: false, message: "", action: "follow" });

  // Filtrer les joueurs selon la recherche
  const handleSearch = (term: string) => {
    setSearchTerm(term);
    if (term.length >= 2) {
      const filtered = players.filter((player) =>
        `${player.name} ${player.surname} ${player.licenceId}`
          .toLowerCase()
          .includes(term.toLowerCase())
      );
      setSearchResults(filtered);
    } else {
      setSearchResults([]);
    }
  };

  // Mettre à jour la liste des joueurs suivis
  useEffect(() => {
    const followed = players.filter(
      (player) => player.id && followedPlayers.includes(player.id)
    );
    setFollowedPlayersList(followed);
  }, [players, followedPlayers]);

  // Gérer le basculement du suivi d'un joueur
  const handleToggleFollow = async (playerId: string, playerName: string) => {
    try {
      const isCurrentlyFollowed = isPlayerFollowed(playerId);
      await dispatch(toggleFollowedPlayer({ userId, playerId })).unwrap();

      // Afficher un feedback
      setSnackbar({
        open: true,
        message: isCurrentlyFollowed
          ? `${playerName} n'est plus suivi`
          : `${playerName} est maintenant suivi`,
        action: isCurrentlyFollowed ? "unfollow" : "follow",
      });

      // Mettre à jour la liste des résultats de recherche
      if (searchResults.length > 0) {
        const updatedResults = searchResults.map((player) => {
          if (player.id === playerId) {
            return { ...player };
          }
          return player;
        });
        setSearchResults(updatedResults);
      }
    } catch (error) {
      console.error("Erreur lors du basculement du suivi:", error);
      setSnackbar({
        open: true,
        message: "Erreur lors de la modification du suivi",
        action: "follow",
      });
    }
  };

  // Vérifier si un joueur est suivi
  const isPlayerFollowed = (playerId: string) => {
    return followedPlayers.includes(playerId);
  };

  // Obtenir le nombre de joueurs suivis
  const followedCount = followedPlayers.length;

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Section de recherche */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Rechercher un joueur à suivre
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Tapez le nom, prénom ou numéro de licence d'un joueur pour le suivre
        </Typography>

        <TextField
          fullWidth
          variant="outlined"
          placeholder="Rechercher un joueur (nom, prénom, licence)..."
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => handleSearch("")}
                  edge="end"
                  aria-label="Vider la recherche"
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Résultats de recherche */}
        {searchResults.length > 0 && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Résultats de recherche ({searchResults.length})
            </Typography>
            <List dense>
              {searchResults.map((player) => {
                const isFollowed = isPlayerFollowed(player.id || "");
                return (
                  <ListItem
                    key={player.id}
                    sx={{
                      border: 1,
                      borderColor: isFollowed ? "success.main" : "divider",
                      borderRadius: 1,
                      mb: 1,
                      backgroundColor: isFollowed
                        ? "success.50"
                        : "transparent",
                      transition: "all 0.2s ease-in-out",
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography variant="body1">
                            {player.name} {player.surname}
                          </Typography>
                          {isFollowed && (
                            <CheckCircleIcon color="success" fontSize="small" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Licence: {player.licenceId}
                          </Typography>
                          {player.club && (
                            <Typography variant="body2" color="text.secondary">
                              Club: {player.club}
                            </Typography>
                          )}
                          {player.dossard && (
                            <Typography variant="body2" color="text.secondary">
                              Dossard: {player.dossard}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      {isFollowed ? (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<RemoveIcon />}
                          onClick={() =>
                            player.id &&
                            handleToggleFollow(
                              player.id,
                              `${player.name} ${player.surname}`
                            )
                          }
                          disabled={loading}
                        >
                          Ne plus suivre
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() =>
                            player.id &&
                            handleToggleFollow(
                              player.id,
                              `${player.name} ${player.surname}`
                            )
                          }
                          disabled={loading}
                        >
                          Suivre
                        </Button>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}

        {searchTerm.length >= 2 && searchResults.length === 0 && (
          <Alert severity="info">Aucun joueur trouvé pour "{searchTerm}"</Alert>
        )}
      </Paper>

      {/* Section des joueurs suivis */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Joueurs suivis
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Vous recevrez des notifications pour les matchs de ces joueurs
          </Typography>

          {followedCount > 0 && (
            <Chip
              icon={<PersonIcon />}
              label={`${followedCount} joueur${
                followedCount > 1 ? "s" : ""
              } suivi${followedCount > 1 ? "s" : ""}`}
              color="primary"
              variant="outlined"
              sx={{ mb: 2 }}
            />
          )}
        </Box>

        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{ py: 4 }}
          >
            <CircularProgress />
          </Box>
        ) : followedPlayersList.length === 0 ? (
          <Alert severity="info">
            Vous ne suivez aucun joueur pour le moment. Utilisez la recherche
            ci-dessus pour commencer à suivre des joueurs.
          </Alert>
        ) : (
          <List>
            {followedPlayersList.map((player, index) => (
              <React.Fragment key={player.id || index}>
                <ListItem
                  disableGutters
                  sx={{ alignItems: "flex-start", py: 2 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      width: "100%",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      flexWrap: { xs: "wrap", sm: "nowrap" },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {player.name} {player.surname}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Licence: {player.licenceId}
                      </Typography>
                      {player.club && (
                        <Typography variant="body2" color="text.secondary">
                          Club: {player.club}
                        </Typography>
                      )}
                      {player.dossard && (
                        <Typography variant="body2" color="text.secondary">
                          Dossard: {player.dossard}
                        </Typography>
                      )}
                    </Box>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<RemoveIcon />}
                      onClick={() =>
                        player.id &&
                        handleToggleFollow(
                          player.id,
                          `${player.name} ${player.surname}`
                        )
                      }
                      disabled={loading}
                      sx={{ whiteSpace: "nowrap", minWidth: 0 }}
                    >
                      Ne plus suivre
                    </Button>
                  </Box>
                </ListItem>
                {index < followedPlayersList.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      {/* Snackbar pour les feedbacks */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
};

export default PlayerFollowList;
